<template>
    <section class="section-lesson-embed" :class="color">
        <LayoutContained :class="padding">
            <ContentHeading
                v-if="heading"
                :heading="heading"
                :headingColor="headingColor"
                :headingComponent="headingComponent"
                :textCentered="textCentered"
                marginBottom="mb-1"
            ></ContentHeading>
            <p v-if="lesson?.lesson_course" class="text-white">Course: {{lesson.lesson_course.course_title}}</p>
            <div class="mt-4">
                <EmbedFileVideoTranscoded v-if="lesson.lesson_video_platform === 'cloudfront_private'" :file="sourcesToVideoTranscoded" :overridePoster="lesson?.lesson_image_url"></EmbedFileVideoTranscoded>
                <VideoGeneralCloudfront v-else-if="lesson.lesson_video_platform === 'cloudfront_public'" :src="sourcesToVideoGeneral" :overridePoster="lesson?.lesson_image_url"></VideoGeneralCloudfront>
                <EmbedYoutubeUrl v-else-if="lesson.lesson_video_platform === 'youtube'" :url="sourcesToYoutubeEmbed" :poster="lesson?.lesson_image_url"></EmbedYoutubeUrl>
                <EmbedLocked v-else-if="lesson.lesson_locked" :poster="lesson?.lesson_image_url"></EmbedLocked>
            </div>
            <div v-if="!isLoggedIn" class="flex flex-wrap -mx-1.5 mt-3">
                <div class="w-full sm:w-1/2 md:w-1/3 px-1 sm:px-1.5 mb-2">
                    <ButtonRegular
                        class="w-full text-center text-sm lg:text-lg"
                        :buttonRoute="useContextConversion().routeMembership(lesson?.lesson_locked ? 'video-locked-action' : 'video-unlocked-action')"
                        buttonColor="bg-amber-800 font-semibold text-white hover:bg-amber-700 focus-visible:outline-amber-600 text-sm lg:text-lg"
                        buttonText="Accept Free Trial"
                    ></ButtonRegular>
                </div>
                <div class="w-full sm:w-1/2 md:w-1/3 px-1 sm:px-1.5 mb-2">
                    <ButtonLocked
                        class="w-full text-center text-sm lg:text-lg"
                        buttonText="Add to My List"
                        button-color="bg-amber-800 font-semibold text-white hover:bg-amber-700 focus-visible:outline-amber-600 text-sm lg:text-lg"
                    ></ButtonLocked>
                </div>
                <div class="w-full sm:w-1/2 md:w-1/3 px-1 sm:px-1.5 mb-2" v-if="lesson?.lesson_course?.course_songs.length">
                    <ButtonRegular
                        class="w-full text-center text-sm lg:text-lg"
                        buttonText="Course Songs"
                        buttonColor="bg-amber-800 font-semibold text-white hover:bg-amber-700 focus-visible:outline-amber-600 text-sm lg:text-lg"
                        :buttonRoute="`/courses/${lesson.lesson_course.course_slug}/#course-songs`"
                    ></ButtonRegular>
                </div>
                <div class="w-full sm:w-1/2 md:w-1/3 px-1 sm:px-1.5 mb-2">
                    <ButtonLocked
                        class="w-full text-center text-sm lg:text-lg"
                        v-if="lesson.lesson_chords.length"
                        buttonText="Browse Chord Sheets"
                        button-color="bg-amber-800 font-semibold text-white hover:bg-amber-700 focus-visible:outline-amber-600 text-sm lg:text-lg"
                    ></ButtonLocked>
                </div>
            </div>
            <div v-if="isLoggedIn" class="flex flex-wrap -mx-1.5 mt-3">
                <div class="w-full sm:w-1/2 md:w-1/3 px-1 sm:px-1.5 mb-2" v-if="currentUser?.eligible_for_aaa_free_trial">
                    <ButtonRegular
                        class="w-full h-full text-center text-sm lg:text-lg"
                        buttonColor="bg-amber-800 font-semibold text-white hover:bg-amber-700 focus-visible:outline-amber-600 text-sm lg:text-lg"
                        :buttonRoute="useContextConversion().routeMembership(lesson?.lesson_locked ? 'video-locked-action' : 'video-unlocked-action')"
                        buttonText="Accept Free Trial"
                    ></ButtonRegular>
                </div>
                <div class="w-full sm:w-1/2 md:w-1/3 px-1 sm:px-1.5 mb-2">
                    <ButtonRegular
                        class="w-full h-full text-center text-sm lg:text-lg"
                        buttonColor="bg-amber-800 font-semibold text-white hover:bg-amber-700 focus-visible:outline-amber-600 text-sm lg:text-lg"
                        :buttonText="lesson?.saved_to_my_list ? 'Remove from My List' : 'Add to My List'"
                        :buttonClick="lesson?.saved_to_my_list ? removeFromMyList : addToMyList"
                        :buttonDisabled="disabled"
                    ></ButtonRegular>
                </div>
                <div class="w-full sm:w-1/2 md:w-1/3 px-1 sm:px-1.5 mb-2" v-if="lesson?.lesson_course?.course_songs.length">
                    <ButtonRegular
                        class="w-full h-full text-center text-sm lg:text-lg"
                        buttonText="Course Songs"
                        buttonColor="bg-amber-800 font-semibold text-white hover:bg-amber-700 focus-visible:outline-amber-600 text-sm lg:text-lg"
                        :buttonRoute="`/courses/${lesson.lesson_course.course_slug}/#course-songs`"
                    ></ButtonRegular>
                </div>
                <div class="w-full sm:w-1/2 md:w-1/3 px-1 sm:px-1.5 mb-2" v-if="isEditor">
                    <ButtonRegular
                        class="w-full h-full text-center"
                        buttonText="Edit"
                        target="_blank"
                        buttonColor="bg-amber-800 font-semibold text-white hover:bg-amber-700 focus-visible:outline-amber-600 text-sm lg:text-lg"
                        :buttonHref="$config.public.admin.lesson + lesson.id_lesson"
                    ></ButtonRegular>
                </div>
            </div>
        </LayoutContained>
    </section>
</template>

<script>
import {mapState, mapActions} from 'pinia';

export default {
    name: 'SectionLessonEmbed',
    props: {
        lesson: {
            required: true,
            type: Object,
            default () {
                return {};
            },
        },
        padding: {
            required: false,
            default: 'py-5 sm:py-11',
            type: String,
        },
        color: {
            required: false,
            type: String,
            default: 'bg-zinc-800',
        },
        headingColor: {
            required: false,
            type: String,
            default: 'text-white',
        },
        headingComponent: {
            required: false,
            type: String,
            default: 'h1',
        },
        textCentered: {
            required: false,
            type: [Boolean, Number],
            default: false,
        },
    },
    emits: ['refresh'],
    computed: {
        ...mapState(useStoreAuth, {
            currentUser: 'currentUser',
            isLoggedIn: 'isLoggedIn',
            isEditor: 'isEditor',
        }),
        ...mapState(useStoreUserLessons, {
            disabled: 'disabled'
        }),
        heading () {
            return this.lesson.lesson_title;
        },
        sourcesToVideoTranscoded () {
            // Test Dev - 01-the-e-chord
            return this.lesson?.lesson_video_sources_secure;
        },
        sourcesToVideoGeneral () {
            // Test Dev - john-mayer-why-georgia
            return this.lesson?.lesson_video_content_url;
        },
        sourcesToYoutubeEmbed () {
            // Test Dev - the-smiths-how-soon-is-now
            return this.lesson?.lesson_video_embed_url;
        },
    },
    methods: {
        ...mapActions(useStoreUserLessons, {
            postUserListLesson: 'postUserListLesson',
            deleteUserListLesson: 'deleteUserListLesson',
        }),
        addToMyList () {
            this.postUserListLesson(this.lesson.id_lesson);
        },
        removeFromMyList () {
            this.deleteUserListLesson(this.lesson.id_lesson)
                .then(() => {
                    this.$emit('refresh');
                });
        },
    },
};
</script>
